/** SA-1060 (spec 2026-10-09 §2.2): the process editors' card ids, their
 * element anchors and the per-viewer open state. One pair of functions
 * names an anchor and reads it back, so a deep link and a card can never
 * disagree; the hook keeps every card closed unless the viewer opened it,
 * remembered in localStorage under `process-editor:<workflow id>:<card id>`. */
import { useCallback, useMemo, useState } from 'react'

export type CardId = string

export const SEATS_CARD: CardId = 'seats'
export const FIELDS_CARD: CardId = 'fields'
export const LANES_CARD: CardId = 'lanes'
export const stepCard = (stepId: string): CardId => `step:${stepId}`
export const aiCard = (stepId: string): CardId => `step:${stepId}:ai`
export const contractCard = (stepId: string): CardId => `step:${stepId}:contract`

const SECTION_CARDS = new Set<CardId>([SEATS_CARD, FIELDS_CARD, LANES_CARD])
/** The open map of a workflow this hook has not touched: ONE object, so the hook's return stays stable. */
const NONE: Record<CardId, boolean> = {}

/** The element id a card roots (`process-step-<id>`, `process-seats`, …). */
export function cardAnchor(cardId: CardId): string {
  const step = /^step:([^:]+)$/.exec(cardId)
  if (step) return `process-step-${step[1]}`
  return `process-${cardId}`
}

/** The inverse of `cardAnchor` for the four card kinds that have an anchor. */
export function anchorCard(elementId: string): CardId | null {
  const step = /^process-step-(.+)$/.exec(elementId)
  if (step) return stepCard(step[1])
  const section = elementId.startsWith('process-') ? elementId.slice('process-'.length) : ''
  return SECTION_CARDS.has(section) ? section : null
}

export const cardStorageKey = (workflowId: string, cardId: CardId): string =>
  `process-editor:${workflowId}:${cardId}`

/** `steps.<id>…` → that step's card; `fields.` / `seats.` / `lanes.` → the
 * section's card; else null (a document-level finding opens nothing). The
 * draft bar's finding buttons read it; `utils/workflowConfigView.processPathAnchor`
 * spells the same four heads as anchors, and `editorCards.test.ts` pins the
 * two against each other. */
export function findingCard(path: string): CardId | null {
  const m = /^steps\.([^.]+)/.exec(path)
  if (m) return stepCard(m[1])
  if (path.startsWith('fields.')) return FIELDS_CARD
  if (path.startsWith('seats.')) return SEATS_CARD
  if (path.startsWith('lanes.')) return LANES_CARD
  return null
}

export interface EditorCards {
  isOpen(cardId: CardId): boolean
  setOpen(cardId: CardId, open: boolean): void
  /** What a deep link calls: opens and remembers. */
  open(cardId: CardId): void
  openAll(cardIds: CardId[]): void
  closeAll(cardIds: CardId[]): void
}

function readStored(workflowId: string, cardId: CardId): boolean {
  try {
    return window.localStorage.getItem(cardStorageKey(workflowId, cardId)) === 'open'
  } catch {
    return false
  }
}

function writeStored(workflowId: string, cardId: CardId, open: boolean): void {
  try {
    window.localStorage.setItem(cardStorageKey(workflowId, cardId), open ? 'open' : 'closed')
  } catch {
    // A blocked storage is a per-device convenience lost, never an error.
  }
}

interface OpenState {
  workflowId: string
  open: Record<CardId, boolean>
}

export function useEditorCards(workflowId: string): EditorCards {
  const [state, setState] = useState<OpenState>({ workflowId, open: {} })
  // Another workflow starts from an empty map; its stored values are read lazily.
  const open = state.workflowId === workflowId ? state.open : NONE

  const apply = useCallback(
    (patch: Record<CardId, boolean>) => {
      for (const [id, isOpen] of Object.entries(patch)) writeStored(workflowId, id, isOpen)
      setState((s) => ({ workflowId, open: { ...(s.workflowId === workflowId ? s.open : {}), ...patch } }))
    },
    [workflowId],
  )

  return useMemo<EditorCards>(
    () => ({
      isOpen: (id) => open[id] ?? readStored(workflowId, id),
      setOpen: (id, isOpen) => apply({ [id]: isOpen }),
      open: (id) => apply({ [id]: true }),
      openAll: (ids) => apply(Object.fromEntries(ids.map((id) => [id, true]))),
      closeAll: (ids) => apply(Object.fromEntries(ids.map((id) => [id, false]))),
    }),
    [open, workflowId, apply],
  )
}

/** Test helper: every card open, nothing remembered. */
export function allOpen(): EditorCards {
  return { isOpen: () => true, setOpen: () => {}, open: () => {}, openAll: () => {}, closeAll: () => {} }
}
